import React from 'react';
import { Users, Building2, User, type LucideIcon } from 'lucide-react';
import { SelectionType } from 'interfaces/settings';

export interface HierarchyColorScheme {
    icon: LucideIcon;
    label: string;
    bgColor: string;
    borderColor: string;
    iconColor: string;
    textColor: string;
    valueColor: string;
    labelColor: string;
    labelBgColor: string;
}

/**
 * Standardized color scheme for different hierarchy levels in autodialer settings
 * - Team level: Blue colors
 * - Subteam level: Purple colors  
 * - User level: Green colors
 * - Default/Other: Gray colors
 */
export const HIERARCHY_COLORS = {
    TEAM: {
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-300',
        iconColor: 'text-blue-600',
        textColor: 'text-blue-900',
        valueColor: '#1d4ed8',
        labelColor: '#1e3a8a',
        labelBgColor: 'bg-blue-100'
    },
    SUBTEAM: {
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        iconColor: 'text-purple-600',
        textColor: 'text-purple-800',
        valueColor: '#7c3aed',
        labelColor: '#581c87',
        labelBgColor: 'bg-purple-100'
    },
    USER: {
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        iconColor: 'text-green-600',
        textColor: 'text-green-800',
        valueColor: '#059669',
        labelColor: '#065f46',
        labelBgColor: 'bg-green-100'
    },
    DEFAULT: {
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        iconColor: 'text-gray-600',
        textColor: 'text-gray-800',
        valueColor: '#4b5563',
        labelColor: '#374151',
        labelBgColor: 'bg-gray-100'
    }
} as const;

/**
 * Get standardized colors and styling for a given selection type
 * @param selectionType - The type of selection (team, subteam, user, etc.)
 * @param subgroupName - Optional subgroup name for subteam selections
 * @param userName - Optional user name for user selections
 * @returns Complete styling information including icon, label, and colors
 */
export function getHierarchyColorsAndIcon(
    selectionType: SelectionType,
    subgroupName?: string,
    userName?: string
): HierarchyColorScheme {
    switch (selectionType) {
        case SelectionType.OVERALL_TEAM:
            return {
                icon: Building2,
                label: 'Set by team',
                ...HIERARCHY_COLORS.TEAM
            };
        
        case SelectionType.SUB_TEAM:
            return {
                icon: Users,
                label: subgroupName ? `Set by subteam - ${subgroupName}` : 'Set by subteam',
                ...HIERARCHY_COLORS.SUBTEAM
            };
        
        case SelectionType.USER:
            return {
                icon: User,
                label: 'Set by user',
                ...HIERARCHY_COLORS.USER
            };
        
        case SelectionType.ME:
            return {
                icon: User,
                label: userName ? `Set by ${userName}` : 'Set by user',
                ...HIERARCHY_COLORS.USER
            };
        
        default:
            return {
                icon: User,
                label: 'Set by other',
                ...HIERARCHY_COLORS.DEFAULT
            };
    }
}

/**
 * Get compact color classes for small displays (e.g., mapping tables)
 * @param selectionType - The type of selection
 * @returns Object with background, border, and text color classes
 */
export function getCompactHierarchyColors(selectionType: SelectionType) {
    const colorScheme = getHierarchyColorsAndIcon(selectionType);
    return {
        bgColor: selectionType === SelectionType.OVERALL_TEAM 
            ? 'bg-blue-200' 
            : colorScheme.bgColor.replace('-50', '-100'), // Darker blue for team, regular for others
        borderColor: colorScheme.borderColor,
        textColor: colorScheme.textColor
    };
}